import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';
import { s as setInputRef, g as getError, a as getValidityObject, c as checkValidity, r as reportValidity, b as setCustomValidity, w as watchError, d as watchWarning, e as getHostAttributes, f as getFormFieldAttributes } from './index7.js';
import { t as toLabel, a as toValue } from './index5.js';
import { g as getDataAttributes } from './data-attributes.js';
import { d as defineCustomElement$7 } from './box.js';
import { d as defineCustomElement$6 } from './form-field.js';
import { d as defineCustomElement$5 } from './icon.js';
import { d as defineCustomElement$4 } from './radio-button.js';
import { d as defineCustomElement$3 } from './tooltip.js';
import { d as defineCustomElement$2 } from './tooltip-label.js';

const radioGroupCss = ":host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;box-sizing:border-box;display:flex;flex:none;font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal;width:-webkit-fit-content;width:fit-content}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}.inner{display:flex;flex:1}:host([orientation=horizontal]) .inner{flex-direction:row;gap:1.5rem}:host([orientation=vertical]) .inner{flex-direction:column;gap:.75rem}:host([orientation=vertical]) wui-radio-button{width:100%}:host([size=lg]) .inner{gap:.75rem}";
const WuiRadioGroupStyle0 = radioGroupCss;

const WuiRadioGroup$1 = /*@__PURE__*/ proxyCustomElement(class WuiRadioGroup extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.validityChecked = createEvent(this, "validityChecked", 7);
        this.validityReported = createEvent(this, "validityReported", 7);
        this.setInputRef = setInputRef(this);
        this.dirty = false;
        this.getError = getError(this);
        this.handleInput = (e) => {
            // Stop inner radio button event from bubbling up
            e.stopPropagation();
            const target = e.target;
            if (!target) {
                return;
            }
            this.value = target.value;
            this.dirty = true;
            this.checkValidity();
            this.el.dispatchEvent(new window.Event('input', { bubbles: true }));
        };
        this.value = undefined;
        this.required = false;
        this.error = undefined;
        this.warning = undefined;
        this.validate = undefined;
        this.validity = getValidityObject();
        this.validationMessage = '';
        this.customValidation = undefined;
        this.checkValidity = checkValidity(this);
        this.reportValidity = reportValidity(this);
        this.setCustomValidity = setCustomValidity(this);
        this.showValidity = false;
        this.formField = undefined;
        this.name = undefined;
        this.options = [];
        this.orientation = 'vertical';
        this.size = 'md';
        this.disabled = false;
    }
    _watchError(...args) {
        watchError(this, ...args);
    }
    _watchWarning(...args) {
        watchWarning(this, ...args);
    }
    renderInner() {
        return (h("div", { class: "inner" }, h("input", { hidden: true, type: "text", value: this.value, required: !!this.required, disabled: this.disabled, ref: this.setInputRef, onInput: (e) => {
                e.stopPropagation();
            }, onChange: (e) => {
                e.stopPropagation();
            } }), this.options?.map((option, idx) => {
            // Support string as well as RadioOption object
            const label = toLabel(option);
            const value = toValue(option);
            const disabled = typeof option === 'object' ? option.disabled : this.disabled;
            const description = typeof option === 'object' ? option.description : undefined;
            const imageSrc = typeof option === 'object' ? option.imageSrc : undefined;
            const imageAlt = typeof option === 'object' ? option.imageAlt : undefined;
            return (h("wui-radio-button", { key: idx, label: label, description: this.size === 'md' ? undefined : description, formField: this.size === 'md' && description ? { description } : undefined, value: value, disabled: disabled || this.disabled, required: this.required, name: this.name ? `${this.name}[]` : undefined, imageSrc: imageSrc, imageAlt: imageAlt, onChange: (e) => {
                    // Prevent change event from bubbling up
                    e.stopPropagation();
                }, onInput: this.handleInput, checked: value === this.value, ...(typeof option === 'object' ? getDataAttributes(option) : {}) }));
        })));
    }
    render() {
        return (h(Host, { key: '8592a37c93f43079dabbcb4d44a923c70f12e5af', ...getHostAttributes(this) }, h("wui-form-field", { key: 'cb427ea930f5e9902dd1caeac745b109a00eb285', ...getFormFieldAttributes(this) }, this.renderInner())));
    }
    get el() { return this; }
    static get watchers() { return {
        "error": ["_watchError"],
        "warning": ["_watchWarning"]
    }; }
    static get style() { return WuiRadioGroupStyle0; }
}, [1, "wui-radio-group", {
        "value": [1025],
        "required": [8],
        "error": [8],
        "warning": [8],
        "validate": [16],
        "validity": [1040],
        "validationMessage": [1025, "validation-message"],
        "checkValidity": [16],
        "reportValidity": [16],
        "setCustomValidity": [16],
        "formField": [16],
        "name": [1],
        "options": [16],
        "orientation": [513],
        "size": [513],
        "disabled": [4],
        "customValidation": [32],
        "showValidity": [32]
    }, undefined, {
        "error": ["_watchError"],
        "warning": ["_watchWarning"]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-radio-group", "wui-box", "wui-form-field", "wui-icon", "wui-radio-button", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-radio-group":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiRadioGroup$1);
            }
            break;
        case "wui-box":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "wui-form-field":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "wui-radio-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}
defineCustomElement$1();

const WuiRadioGroup = WuiRadioGroup$1;
const defineCustomElement = defineCustomElement$1;

export { WuiRadioGroup, defineCustomElement };

//# sourceMappingURL=wui-radio-group.js.map